// frontend/src/components/EditWareHouses.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Warehouse, Building, MapPin, Save, X, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./WareHouses.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditWareHouses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userType, company } = useAuth();

  const [formData, setFormData] = useState({ 
    warehouse_code: "", 
    warehouse_name: "", 
    location: "" 
  });
  const [warehouse, setWarehouse] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});

  useEffect(() => {
    const fetchWarehouse = async () => {
      try {
        const res = await axios.get(`${url}/api/warehouse/${id}`);
        
        if (res.data.success) {
          const data = res.data.data;
          setWarehouse(data);
          setFormData({ 
            warehouse_code: data.warehouse_code, 
            warehouse_name: data.warehouse_name, 
            location: data.location || "" 
          });
        } else {
          toast.error(res.data.message || "Failed to load warehouse");
          navigate("/ViewWareHouses");
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load warehouse");
        navigate("/ViewWareHouses");
      } finally {
        setLoading(false);
      }
    };
    fetchWarehouse();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: name === 'warehouse_code' ? value.toUpperCase() : value 
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) =>
    setFocused({ ...focused, [field]: formData[field] && formData[field].length > 0 });

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.warehouse_code.trim()) {
      newErrors.warehouse_code = "Warehouse code is required";
    } else if (!/^WH\d{3}$/i.test(formData.warehouse_code)) {
      newErrors.warehouse_code = "Warehouse code must be in WH001 format";
    }
    
    if (!formData.warehouse_name.trim()) {
      newErrors.warehouse_name = "Warehouse name is required";
    } else if (formData.warehouse_name.length > 100) {
      newErrors.warehouse_name = "Warehouse name cannot exceed 100 characters";
    }
    
    if (formData.location && formData.location.length > 200) {
      newErrors.location = "Location cannot exceed 200 characters";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.put(`${url}/api/warehouse/${id}`, formData);
      
      if (res.data.success) {
        toast.success("Warehouse updated successfully");
        navigate("/ViewWareHouses");
      } else {
        toast.error(res.data.message || "Update failed");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Update failed";
      toast.error(errorMsg);
      
      if (err.response?.status === 409) {
        setErrors(prev => ({
          ...prev,
          warehouse_code: "This warehouse code already exists for this company"
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewWareHouses");
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="warehouses-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading warehouse details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className="form-wrapper">
        <div className="warehouses-form-container">
          <div className="error-container" style={{ textAlign: 'center', padding: '40px' }}>
            <AlertCircle size={32} color="#dc3545" />
            <p style={{ margin: '15px 0', color: '#666' }}>Warehouse not found</p>
            <button className="cancel-btn" onClick={handleCancel}>
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canEdit = warehouse.status === "active";

  return (
    <div className="form-wrapper">
      <div className="warehouses-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Close"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <Warehouse size={24} />
          <h2 className="form-title">Edit Warehouse</h2>
          <p className="form-subtitle">
            Update warehouse information
          </p>
        </div>

     
        {!canEdit && (
          <div className="alert alert-warning" style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            color: '#856404'
          }}>
            <AlertCircle size={16} className="mr-2" />
            This warehouse is inactive and cannot be edited.
          </div>
        )}

        <form className="warehouses-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Warehouse Code - Read-only for editing */}
            <div className={`form-field required filled ${errors.warehouse_code ? 'error' : ''}`}>
              <input
                type="text"
                name="warehouse_code"
                value={formData.warehouse_code}
                onChange={handleChange}
                onFocus={() => handleFocus("warehouse_code")}
                onBlur={() => handleBlur("warehouse_code")}
                placeholder="WH001, WH002, etc."
                required
                maxLength={6}
                style={{ 
                  textTransform: 'uppercase', 
                  backgroundColor: '#f8f9fa'
                }}
                readOnly={!canEdit}
              />
              <label>Warehouse Code</label>
              {errors.warehouse_code && <small className="field-error">{errors.warehouse_code}</small>}
              <small className="field-hint">
                Warehouse code cannot be changed
              </small>
            </div>

            {/* Warehouse Name - Editable */}
            <div className={`form-field required ${focused.warehouse_name || formData.warehouse_name ? "filled" : ""} ${errors.warehouse_name ? 'error' : ''}`}>
              <input
                type="text"
                name="warehouse_name"
                value={formData.warehouse_name}
                onChange={handleChange}
                onFocus={() => handleFocus("warehouse_name")}
                onBlur={() => handleBlur("warehouse_name")}
                placeholder="Enter warehouse name"
                required
                maxLength={100}
                disabled={!canEdit}
              />
              <label>Warehouse Name</label>
              {errors.warehouse_name && <small className="field-error">{errors.warehouse_name}</small>}
              <small className="field-hint">
                Descriptive name for the warehouse (max 100 characters)
              </small>
            </div>

            {/* Location - Editable */}
            <div className={`form-field ${focused.location || formData.location ? "filled" : ""} ${errors.location ? 'error' : ''}`}>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                onFocus={() => handleFocus("location")}
                onBlur={() => handleBlur("location")}
                placeholder="Enter location/address"
                maxLength={200}
                disabled={!canEdit}
              />
              <label>
                <MapPin size={14} className="mr-2" />
                Location
              </label>
              {errors.location && <small className="field-error">{errors.location}</small>}
              <small className="field-hint">
                Physical address or location description (max 200 characters)
              </small>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || !canEdit}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Updating...
                </>
              ) : (
                <>
                  Update Warehouse
                  <Save className="ml-2" size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditWareHouses;